/**
 * BusinessSwitcher — compact dropdown chip in the header that lets a
 * user flip context between "Individual (posting as myself)" and any
 * business they own or manage. Silently returns null when the user
 * has no businesses.
 *
 * When active, the chip renders the business avatar (or initial) plus
 * name; picking a business writes the id to localStorage via
 * ``useMyBusinesses`` so subsequent write actions (compose story,
 * upload product, etc.) can attribute to the business.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronDown, Check, User as UserIcon, Plus, ExternalLink } from "lucide-react";
import { useMyBusinesses } from "@/lib/useMyBusinesses";

export function BusinessSwitcher() {
  const { items, active, setActive } = useMyBusinesses();
  const [open, setOpen] = useState(false);

  if (!items.length) return null;   // no businesses → no chip

  return (
    <div className="relative inline-block" data-testid="business-switcher">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium hover:bg-black/5 transition-colors"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid="business-switcher-toggle"
        aria-label="Switch posting context"
        title={active ? `Posting as ${active.name}` : "Posting as yourself"}
      >
        {active ? (
          <>
            <Building2 className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
            <span className="max-w-[110px] truncate">{active.name}</span>
          </>
        ) : (
          <>
            <UserIcon className="w-3.5 h-3.5 opacity-70" />
            <span className="opacity-70">Individual</span>
          </>
        )}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <div
          className="absolute right-0 z-30 mt-1 w-64 rounded-lg border bg-white shadow-lg overflow-hidden"
          style={{ borderColor: "var(--wofy-border)" }}
          onMouseDown={(e) => e.preventDefault()}
          data-testid="business-switcher-menu"
        >
          <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider border-b" style={{ color: "var(--wofy-text-soft)", borderColor: "var(--wofy-border)" }}>
            Post as
          </div>
          <button
            type="button"
            onClick={() => { setActive(null); setOpen(false); }}
            className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-black/5"
            data-testid="switch-to-individual"
          >
            <span className="inline-flex items-center gap-2"><UserIcon className="w-4 h-4 opacity-60" /> Myself (Individual)</span>
            {!active && <Check className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />}
          </button>
          <div className="border-t" style={{ borderColor: "var(--wofy-border)" }} />
          {items.map((biz) => (
            <button
              key={biz.id}
              type="button"
              onClick={() => { setActive(biz.id); setOpen(false); }}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-black/5"
              data-testid={`switch-to-business-${biz.id}`}
            >
              <span className="inline-flex items-center gap-2 min-w-0">
                <Building2 className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
                <span className="truncate">{biz.name}</span>
                <span className="text-[9px] font-mono uppercase opacity-50">{biz._role}</span>
              </span>
              {active?.id === biz.id && <Check className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />}
            </button>
          ))}
          <div className="border-t" style={{ borderColor: "var(--wofy-border)" }} />
          {active && (
            <Link
              to={`/business/${active.slug}`}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between px-3 py-2 text-xs hover:bg-black/5"
              data-testid="switch-open-business"
            >
              <span>Open {active.name}</span>
              <ExternalLink className="w-3 h-3" />
            </Link>
          )}
          <Link
            to="/businesses/new"
            onClick={() => setOpen(false)}
            className="flex items-center justify-between px-3 py-2 text-xs hover:bg-black/5 font-medium"
            style={{ color: "var(--wofy-primary)" }}
            data-testid="switch-add-business"
          >
            <span className="inline-flex items-center gap-1.5"><Plus className="w-3 h-3" /> Add a business</span>
          </Link>
        </div>
      )}
    </div>
  );
}
